import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getMiniModules,
  getModules,
  createMiniModule,
  updateMiniModule,
  deleteMiniModule,
  MiniModule,
  MiniModuleCreate,
  Module,
} from '../../../services/rbac';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import PermissionButton from '../../../components/PermissionButton';
import AlertDialog from '../../../components/AlertDialog';

const MiniModuleManagement: React.FC = () => {
  const [miniModules, setMiniModules] = useState<MiniModule[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMiniModule, setEditingMiniModule] = useState<MiniModule | null>(null);
  const [formData, setFormData] = useState<MiniModuleCreate>({ name: '', module_id: 0 });
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [miniModsData, modsData] = await Promise.all([getMiniModules(), getModules()]);
      setMiniModules(miniModsData);
      setModules(modsData);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (miniModule?: MiniModule) => {
    if (miniModule) {
      setEditingMiniModule(miniModule);
      setFormData({ name: miniModule.name, module_id: miniModule.module_id });
    } else {
      setEditingMiniModule(null);
      setFormData({ name: '', module_id: modules[0]?.id || 0 });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMiniModule(null);
    setFormData({ name: '', module_id: 0 });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError('Le nom du sous-module est requis');
      return;
    }
    if (!formData.module_id) {
      showError('Veuillez sélectionner un module parent');
      return;
    }

    try {
      if (editingMiniModule) {
        await updateMiniModule(editingMiniModule.id, formData);
        showSuccess('Sous-module modifié avec succès');
      } else {
        await createMiniModule(formData);
        showSuccess('Sous-module créé avec succès');
      }
      handleCloseDialog();
      fetchData();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDelete = (miniModule: MiniModule) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer le sous-module "${miniModule.name}" ? Tous les profils associés seront également supprimés.`,
      async () => {
        try {
          await deleteMiniModule(miniModule.id);
          showSuccess('Sous-module supprimé avec succès');
          fetchData();
        } catch (error: any) {
          showError(extractErrorMessage(error));
        }
      },
      {
        title: 'Confirmer la suppression',
        confirmText: 'Supprimer',
      }
    );
  };

  const moduleColors: { [key: string]: string } = {
    Admin: '#F44336',
    Stock: '#2196F3',
    Config: '#1976D2',
    GRC: '#9C27B0',
    Prod: '#FF9800',
    Logistique: '#607D8B',
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Gestion des Sous-Modules
          </Typography>
          <PermissionButton
            moduleName="Admin"
            operation="add"
            miniModuleName="MiniModules"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Sous-Module
          </PermissionButton>
        </Box>

        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>

                <TableCell sx={modernCellStyles}>Module Parent</TableCell>
                <TableCell sx={modernCellStyles}>Nom du Sous-Module</TableCell>
                <TableCell sx={modernCellStyles}>Date de création</TableCell>
                <TableCell sx={modernCellStyles} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : miniModules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucun sous-module trouvé
                  </TableCell>
                </TableRow>
              ) : (
                miniModules.map((miniModule) => (
                  <TableRow key={miniModule.id} hover>

                    <TableCell>
                      <Chip
                        label={miniModule.module?.name || 'N/A'}
                        sx={{
                          bgcolor: moduleColors[miniModule.module?.name || ''] || '#64748b',
                          color: 'white',
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <strong>{miniModule.name}</strong>
                    </TableCell>
                    <TableCell>
                      {new Date(miniModule.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="Admin"
                        operation="edit"
                        miniModuleName="MiniModules"
                        component="IconButton"
                        onClick={() => handleOpenDialog(miniModule)}
                        size="small"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Admin"
                        operation="delete"
                        miniModuleName="MiniModules"
                        component="IconButton"
                        onClick={() => handleDelete(miniModule)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMiniModule ? 'Modifier le sous-module' : 'Nouveau sous-module'}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Module Parent"
            fullWidth
            value={formData.module_id}
            onChange={(e) => setFormData({ ...formData, module_id: Number(e.target.value) })}
            required
            sx={{ mb: 2 }}
          >
            {modules.map((module) => (
              <MenuItem key={module.id} value={module.id}>
                {module.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du sous-module"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            helperText="Ex: Users, Stock Entry, Categories, Suppliers..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingMiniModule ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default MiniModuleManagement;
